import React, { useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { MdDoneAll, MdKeyboardArrowDown } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { ALLOW_DELETE_ACTIVITIES } from '@config';
import { Tooltip } from '@shared';
import { Button, Card, Dropdown, Menu, S } from '@shared/components';
import { hasNewPermissionSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';
import { newPermissions } from '@shared/newPermissions';

import { filterActivityValuesByPersonGuid } from '../../../../utils';
import styles from './ActivityItem.less';

const MenuItem = Menu.Item;

const ActivityItem = ({
  data,
  onSelect,
  onClone,
  onDelete,
  onJoin,
  showJoinButton,
  showDropDownMenu,
  personGuid,
}) => {
  const [isApproved, setIsApproved] = useState(false);
  const { totalAdults, totalYouths } = data;
  const noPersonsInActivity = !totalAdults && !totalYouths;
  const canApprove = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.APPROVE_ADVANCEMENTS),
  );
  const personExists = useMemo(() => {
    if (showJoinButton) {
      const { registeredYouths, registeredAdults } = data;
      const existingYouth = filterActivityValuesByPersonGuid(
        registeredYouths,
        personGuid,
      );
      const existingAdult = filterActivityValuesByPersonGuid(
        registeredAdults,
        personGuid,
      );
      const [youthInfo] = existingYouth || [];
      const [adultInfo] = existingAdult || [];
      if (youthInfo) {
        setIsApproved(youthInfo.isApproved);
        return !!youthInfo;
      } else if (adultInfo) {
        setIsApproved(adultInfo.isApproved);
        return !!adultInfo;
      } else {
        setIsApproved(false);
        return false;
      }
    }
  }, [data, showJoinButton, personGuid]);

  const dropdown = showDropDownMenu && (
    <div onClick={e => e.stopPropagation()}>
      <Dropdown
        placement="bottomRight"
        overlay={
          <Menu>
            <MenuItem>
              <div onClick={onClone}>
                <FormattedMessage id="progress.common.ActivityItem.duplicate" />
              </div>
            </MenuItem>
            {ALLOW_DELETE_ACTIVITIES && canApprove && noPersonsInActivity && (
              <MenuItem>
                <div onClick={onDelete}>
                  <FormattedMessage id="progress.common.ActivityItem.delete" />
                </div>
              </MenuItem>
            )}
          </Menu>
        }
        trigger={['click']}
      >
        <MdKeyboardArrowDown className={styles.icon} />
      </Dropdown>
    </div>
  );
  const joinButton = showJoinButton && (
    <div>
      {!isApproved && (
        <Button
          className={styles.joinButton}
          type="primary"
          shadow
          shape="round"
          onClick={() => onJoin(personExists)}
        >
          {personExists ? (
            <FormattedMessage id="shared.edit" />
          ) : (
            <FormattedMessage id="progress.common.ActivityItem.join" />
          )}
        </Button>
      )}
      {personExists && isApproved && (
        <Tooltip
          title={intl.formatMessage({
            id: 'progress.common.ActivityItem.approved',
          })}
        >
          <MdDoneAll className={styles.approved} />
        </Tooltip>
      )}
    </div>
  );
  const { name, location, addressLine1 } = data || {};
  return (
    <Card noPadding hoverable shadow className={styles.item} onClick={onSelect}>
      <S size="3" bold colored className={styles.header}>
        {name} {joinButton} {dropdown}
      </S>
      {location && <S size="3">{location}</S>}
      {addressLine1 && <S size="3">{addressLine1}</S>}
    </Card>
  );
};

ActivityItem.defaultProps = {
  showJoinButton: false,
  showDropDownMenu: true,
};

ActivityItem.propTypes = {
  data: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClone: PropTypes.func.isRequired,
  onJoin: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  showDropDownMenu: PropTypes.bool,
  showJoinButton: PropTypes.bool,
  personGuid: PropTypes.string,
};

export default ActivityItem;
